import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { io } from "socket.io-client";
import confetti from "canvas-confetti";
import { config } from "../Components/Config";
import gongSoundUrl from "../Asset/audio/suara_gong.mp3";
import cashSound from "../Asset/audio/cash_sound_1.mp3";
import bgImage from "../Asset/image/bg_image_display.jpg";
import pointstarLogo from "../Asset/image/pointstar_logo.png";
import "./Display.css";

// Adding simple example

const SOCKET_URL =
  process.env.REACT_APP_SOCKET_URL ||
  (config.BASEURL || "http://localhost:3001/").replace(/\/$/, "");

const DISPLAY_DURATION_MS = 10 * 60 * 1000;
const CONFETTI_DURATION_MS = 60 * 1000;

const formatRupiah = (value) => {
  if (value === null || value === undefined || value === "") return "Rp 0";
  const num = typeof value === "string" ? Number(value.replace(/,/g, "")) : Number(value);
  if (Number.isNaN(num)) return "Rp 0";
  return new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    maximumFractionDigits: 0,
  }).format(num);
};

const normalizePayload = (payload) => {
  const nowIso = new Date().toISOString();

  return {
    id:
      payload?.id_quotation ||
      payload?.quotation_id ||
      payload?.quotation_number ||
      `${Date.now()}-${Math.random().toString(16).slice(2)}`,
    salesName: payload?.sales_name || "Unknown Sales",
    quotationTotal: payload?.quotation_total ?? 0,
    companyName: payload?.company_name || payload?.subcompany_name || payload?.client_name || "-",
    wonAt: payload?.quotation_won_date || payload?.won_date || payload?.updated_at || nowIso,
  };
};

const Display = () => {
  const [isConnected, setIsConnected] = useState(false);
  const [isSoundBlocked, setIsSoundBlocked] = useState(true);
  const [latestWin, setLatestWin] = useState(null);
  // for editing
  // const [latestWin, setLatestWin] = useState(DESIGN_PREVIEW_WIN);
  const [fadeKey, setFadeKey] = useState(0);

  // Refs
  const audioCtxRef = useRef(null);
  const audioBufferRef = useRef(null);
  const dismissTimerRef = useRef(null);
  const cashBufferRef = useRef(null);
  const confettiIntervalRef = useRef(null);
  const confettiCanvasRef = useRef(null);
  const confettiInstanceRef = useRef(null);

  // Memoize static values
  const bgStyle = useMemo(() => ({ backgroundImage: `url(${bgImage})` }), []);
  const formattedAmount = useMemo(
    () => (latestWin ? formatRupiah(latestWin.quotationTotal) : null),
    [latestWin],
  );

  // Stable click handler for sound overlay
  const handleUnlockClick = useCallback(() => {
    const ctx = audioCtxRef.current;
    if (ctx && ctx.state === "suspended") {
      ctx.resume().then(() => setIsSoundBlocked(false));
    } else {
      setIsSoundBlocked(false);
    }
  }, []);

  const startConfetti = useCallback(() => {
    clearInterval(confettiIntervalRef.current);

    const fireConfetti = confettiInstanceRef.current || confetti;
    const duration = CONFETTI_DURATION_MS;
    const animationEnd = Date.now() + duration;
    const defaults = {
      startVelocity: 18,
      spread: 360,
      ticks: 100,
      gravity: 0.8,
      scalar: 1,
      zIndex: 0,
      colors: ["#fed255", "#f5d97a", "#ffffff", "#c8952e"],
    };

    function randomInRange(min, max) {
      return Math.random() * (max - min) + min;
    }

    let rafBusy = false;
    confettiIntervalRef.current = setInterval(() => {
      if (rafBusy) return;
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        clearInterval(confettiIntervalRef.current);
        confettiIntervalRef.current = null;
        return;
      }

      rafBusy = true;
      requestAnimationFrame(() => {
        const particleCount = Math.max(20, 200 * (timeLeft / duration));
        fireConfetti({
          ...defaults,
          particleCount,
          origin: { x: randomInRange(0.05, 0.95), y: randomInRange(0.15, 0.85) },
        });
        rafBusy = false;
      });
    }, 600);
  }, []);

  // Create a confetti instance bound to our in-page canvas
  useEffect(() => {
    if (!confettiCanvasRef.current) return;
    confettiInstanceRef.current = confetti.create(confettiCanvasRef.current, {
      resize: true,
      useWorker: true,
    });
    return () => {
      confettiInstanceRef.current?.reset?.();
      confettiInstanceRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (latestWin) {
      startConfetti();
    }
  }, [latestWin, startConfetti]);

  useEffect(() => {
    if (!latestWin) {
      // No win showing — stop confetti if still running
      clearInterval(confettiIntervalRef.current);
      confettiInstanceRef.current?.reset?.();
      return;
    }

    // Clear any existing timer (e.g. from a previous win)
    clearTimeout(dismissTimerRef.current);

    dismissTimerRef.current = setTimeout(() => {
      setLatestWin(null);
    }, DISPLAY_DURATION_MS);

    return () => clearTimeout(dismissTimerRef.current);
  }, [latestWin]);

  // Pause/resume confetti when tab visibility changes (saves CPU on TVs)
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState !== "visible") {
        // Tab hidden — pause confetti interval
        clearInterval(confettiIntervalRef.current);
        confettiIntervalRef.current = null;
      }
      // Confetti will restart on next quotation_won event
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, []);

  useEffect(() => {
    let wakeLock = null;

    const requestWakeLock = async () => {
      try {
        console.log("Testing", navigator);
        if ("wakeLock" in navigator) {
          wakeLock = await navigator.wakeLock.request("screen");
          console.log("Wake Lock acquired — screen will stay on.");
          wakeLock.addEventListener("release", () => {
            console.log("Wake Lock released.");
          });
        }
      } catch (err) {
        console.warn("Wake Lock request failed:", err.message);
      }
    };

    // Re-acquire wake lock when the tab becomes visible again
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        requestWakeLock();
      }
    };

    requestWakeLock();
    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Fallback: periodic activity to prevent TV sleep timers
    const keepAliveInterval = setInterval(() => {
      // Tiny DOM write tricks some TV browsers into thinking there's activity
      const original = document.title;
      document.title = original + " ";
      document.title = original;
    }, 30000);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      clearInterval(keepAliveInterval);
      if (wakeLock) {
        wakeLock.release().catch(() => {});
      }
    };
  }, []);

  useEffect(() => {
    // ── Web Audio API setup ──
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    const ctx = new AudioCtx();
    audioCtxRef.current = ctx;

    // Pre-fetch and decode the MP3 into a reusable AudioBuffer
    fetch(gongSoundUrl)
      .then((res) => res.arrayBuffer())
      .then((arrayBuf) => ctx.decodeAudioData(arrayBuf))
      .then((decoded) => {
        audioBufferRef.current = decoded;
        console.log("Gong sound decoded and ready.");
      })
      .catch((err) => console.error("Failed to load gong sound:", err));

    //Second sound
    fetch(cashSound)
      .then((res) => res.arrayBuffer())
      .then((arrayBuf) => ctx.decodeAudioData(arrayBuf))
      .then((decoded) => {
        cashBufferRef.current = decoded;
        console.log("Cash sound decoded and ready.");
      })
      .catch((err) => console.error("Failed to load cash sound:", err));

    // ── Unlock audio on ANY user interaction ──
    const unlockAudio = () => {
      if (ctx.state === "suspended") {
        ctx.resume().then(() => {
          setIsSoundBlocked(false);
          console.log("AudioContext resumed — sound is now enabled.");
        });
      } else {
        setIsSoundBlocked(false);
      }
    };

    document.addEventListener("click", unlockAudio);
    document.addEventListener("keydown", unlockAudio);
    document.addEventListener("touchstart", unlockAudio);

    if (ctx.state === "running") {
      setIsSoundBlocked(false);
    }

    // ── Socket.IO setup ──
    const socket = io(SOCKET_URL, { transports: ["websocket"] });

    const onConnect = () => setIsConnected(true);
    const onDisconnect = () => setIsConnected(false);

    const onQuotationWon = (data) => {
      const normalized = normalizePayload(data);

      // Play the gong
      const buf = audioBufferRef.current;
      if (ctx && buf) {
        if (ctx.state === "suspended") {
          ctx.resume().then(() => {
            setIsSoundBlocked(false);
            const gongSource = ctx.createBufferSource();
            gongSource.buffer = buf;
            gongSource.connect(ctx.destination);
            gongSource.start(0);

            const cash1 = ctx.createBufferSource();
            cash1.buffer = cashBufferRef.current;
            cash1.connect(ctx.destination);
            cash1.start(0);

            const cash2 = ctx.createBufferSource();
            cash2.buffer = cashBufferRef.current;
            cash2.connect(ctx.destination);
            cash2.start(ctx.currentTime + cashBufferRef.current.duration);

            //   const source = ctx.createBufferSource();
            //   source.buffer = buf;
            //   source.connect(ctx.destination);
            //   source.start(0);
            //
          });
        } else {
          const gongSource = ctx.createBufferSource();
          gongSource.buffer = buf;
          gongSource.connect(ctx.destination);
          gongSource.start(0);

          const cash1 = ctx.createBufferSource();
          cash1.buffer = cashBufferRef.current;
          cash1.connect(ctx.destination);
          cash1.start(0);

          const cash2 = ctx.createBufferSource();
          cash2.buffer = cashBufferRef.current;
          cash2.connect(ctx.destination);
          cash2.start(ctx.currentTime + cashBufferRef.current.duration);
        }
      }

      setLatestWin(normalized);
      setFadeKey((prev) => prev + 1);
    };

    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);
    socket.on("quotation_won", onQuotationWon);

    return () => {
      document.removeEventListener("click", unlockAudio);
      document.removeEventListener("keydown", unlockAudio);
      document.removeEventListener("touchstart", unlockAudio);

      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
      socket.off("quotation_won", onQuotationWon);
      socket.disconnect();

      if (ctx) {
        ctx.close().catch(() => {});
      }
      audioCtxRef.current = null;
      audioBufferRef.current = null;
      cashBufferRef.current = null;
      clearInterval(confettiIntervalRef.current);
      confettiInstanceRef.current?.reset?.();
    };
  }, []);

  return (
    <div className="display-page" style={bgStyle}>
      <canvas ref={confettiCanvasRef} className="display-confetti-canvas" aria-hidden="true" />

      {isSoundBlocked && (
        <div className="display-sound-overlay" onClick={handleUnlockClick}>
          <div style={{ fontSize: "3rem" }}>🔔</div>
          <div style={{ fontSize: "1.5rem", fontWeight: 600 }}>Click anywhere to enable sound</div>
          <div style={{ fontSize: "0.95rem", opacity: 0.7 }}>
            Browser requires one interaction before playing notification sounds
          </div>
        </div>
      )}

      <div className={`display-connection-badge ${isConnected ? "connected" : "disconnected"}`}>
        {isConnected ? "●" : "○"}
      </div>

      <div className="display-content">
        {latestWin ? (
          <div className="display-fade-in" key={fadeKey}>
            <h1 className="display-congrats">Congratulations</h1>
            <hr className="display-divider" />
            <p className="display-message">
              <strong className="display-text-second">{latestWin.salesName}</strong>
              <strong className="display-text-second">
                {" "}
                for successfully closing a strategic partnership
                <br />
                with{" "}
              </strong>
              <strong className="display-text-second">{latestWin.companyName}</strong>{" "}
              <strong className="display-text-second">worth </strong>
              <strong className="display-text-second">{formattedAmount}</strong>
            </p>
          </div>
        ) : (
          <div className="display-awaiting">
            <div className="display-awaiting-icon">🏆</div>
            <div className="display-awaiting-text text-white">Waiting for the next win</div>
            {/* <div className="display-awaiting-sub">Socket: {SOCKET_URL}</div> */}
          </div>
        )}
      </div>

      <div className="display-logo-container">
        <img src={pointstarLogo} alt="PointStar" className="display-logo" />
      </div>
    </div>
  );
};

export default Display;
